import React, { useState, useEffect } from 'react';
import { vendorAPI, categoryAPI } from '../lib/api';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { DataTable } from '../components/DataTable';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Pencil, Trash2, Truck, Mail, Phone, MapPin, Building, Search } from 'lucide-react';
import { toast } from 'sonner';
import { getStatusColor } from '../lib/utils';

export const VendorsPage = () => {
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [useCustomPaymentTerms, setUseCustomPaymentTerms] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    category_supplied: '',
    contact_person: '',
    mobile: '',
    email: '',
    gst_number: '',
    address: '',
    payment_terms: '',
    custom_payment_terms: '',
    notes: '',
    status: 'active'
  });
  const [saving, setSaving] = useState(false);

  // Predefined payment terms options
  const PAYMENT_TERMS_OPTIONS = [
    { value: 'Cash on Delivery', label: 'Cash on Delivery' },
    { value: 'Bill to Bill', label: 'Bill to Bill' },
    { value: '7 Days', label: '7 Days' },
    { value: '15 Days', label: '15 Days' },
    { value: '30 Days', label: '30 Days' },
    { value: '45 Days', label: '45 Days' },
    { value: 'EMI', label: 'EMI' },
    { value: '__other__', label: 'Other' },
  ];

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await vendorAPI.getAll();
      setVendors(response.data);
    } catch (error) {
      toast.error('Failed to fetch vendors');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  useEffect(() => {
    fetchVendors();
    fetchCategories();
  }, []);

  const handleOpenDialog = (vendor = null) => {
    if (vendor) {
      setSelectedVendor(vendor);
      // Check if payment_terms is a predefined option or custom
      const isPredefinedTerm = PAYMENT_TERMS_OPTIONS.some(
        opt => opt.value !== '__other__' && opt.value === vendor.payment_terms
      );
      setUseCustomPaymentTerms(!isPredefinedTerm && vendor.payment_terms);
      setFormData({
        name: vendor.name || '',
        code: vendor.code || '',
        category_supplied: vendor.category_supplied || '',
        contact_person: vendor.contact_person || '',
        mobile: vendor.mobile || '',
        email: vendor.email || '',
        gst_number: vendor.gst_number || '',
        address: vendor.address || '',
        payment_terms: isPredefinedTerm ? vendor.payment_terms : (vendor.payment_terms ? '__other__' : ''),
        custom_payment_terms: !isPredefinedTerm ? (vendor.payment_terms || '') : '',
        notes: vendor.notes || '',
        status: vendor.status || 'active'
      });
    } else {
      setSelectedVendor(null);
      setUseCustomPaymentTerms(false);
      setFormData({
        name: '',
        code: '',
        category_supplied: '',
        contact_person: '',
        mobile: '',
        email: '',
        gst_number: '',
        address: '',
        payment_terms: '',
        custom_payment_terms: '',
        notes: '',
        status: 'active'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedVendor(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      // Prepare data - use custom payment terms if "Other" is selected
      const submitData = {
        ...formData,
        payment_terms: useCustomPaymentTerms ? formData.custom_payment_terms : formData.payment_terms
      };
      // Remove the custom_payment_terms field as backend doesn't need it
      delete submitData.custom_payment_terms;
      
      // Clean empty string values to null
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '') {
          submitData[key] = null;
        }
      });
      
      if (selectedVendor) {
        await vendorAPI.update(selectedVendor.id, submitData);
        toast.success('Vendor updated successfully');
      } else {
        await vendorAPI.create(submitData);
        toast.success('Vendor created successfully');
      }
      handleCloseDialog();
      fetchVendors();
    } catch (error) {
      // Handle validation errors which might be an array
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to save vendor';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await vendorAPI.delete(selectedVendor.id);
      toast.success('Vendor deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedVendor(null);
      fetchVendors();
    } catch (error) {
      toast.error('Failed to delete vendor');
    }
  };

  const filteredVendors = vendors.filter(v => 
    v.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.contact_person?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Vendors</h1>
          <p className="text-muted-foreground mt-1">Manage your suppliers and vendors</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="rounded-full" data-testid="add-vendor-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add Vendor
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search vendors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-vendors-input"
            />
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            module="vendors"
            data={vendors}
            loading={loading}
            title="Vendors Report"
            columns={[
              { key: 'name', label: 'Name', render: (val) => <span className="font-medium">{val}</span> },
              { key: 'code', label: 'Code', render: (val) => val ? <span className="px-2 py-1 bg-muted rounded text-sm font-mono">{val}</span> : '-' },
              { key: 'contact_person', label: 'Contact Person' },
              { key: 'mobile', label: 'Phone', render: (val, vendor) => (
                <div className="flex flex-col gap-1 text-sm">
                  {val && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {val}</span>}
                  {vendor.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {vendor.email}</span>}
                </div>
              )},
              { key: 'category_supplied', label: 'Category' },
              { key: 'address', label: 'Address', render: (val) => val ? <span className="text-sm truncate max-w-[150px]">{val}</span> : '-' },
              { key: 'status', label: 'Status', render: (val) => (
                <Badge className={getStatusColor(val)}>{val}</Badge>
              )},
            ]}
            renderActions={(vendor) => (
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(vendor)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => { setSelectedVendor(vendor); setDeleteDialogOpen(true); }}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            )}
          />
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedVendor ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
            <DialogDescription>
              {selectedVendor ? 'Update vendor details' : 'Add a new vendor/supplier'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Vendor Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="vendor-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Vendor Code</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    data-testid="vendor-code-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_person">Contact Person</Label>
                  <Input
                    id="contact_person"
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    data-testid="vendor-contact-person-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mobile">Mobile</Label>
                  <Input
                    id="mobile"
                    type="tel"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    data-testid="vendor-mobile-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="vendor-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gst_number">GST Number</Label>
                  <Input
                    id="gst_number"
                    value={formData.gst_number}
                    onChange={(e) => setFormData({ ...formData, gst_number: e.target.value.toUpperCase() })}
                    data-testid="vendor-gst-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category_supplied">Category Served</Label>
                  <Select
                    value={formData.category_supplied || ''}
                    onValueChange={(value) => setFormData({ ...formData, category_supplied: value })}
                  >
                    <SelectTrigger data-testid="vendor-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent position="popper" className="max-h-60">
                      {categories.length === 0 ? (
                        <SelectItem value="__none__" disabled>No categories available</SelectItem>
                      ) : (
                        categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.name}>
                            {cat.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger data-testid="vendor-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Textarea
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  rows={2}
                  data-testid="vendor-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="payment_terms">Payment Terms</Label>
                <Select
                  value={formData.payment_terms || ''}
                  onValueChange={(value) => {
                    if (value === '__other__') {
                      setUseCustomPaymentTerms(true);
                      setFormData({ ...formData, payment_terms: '__other__' });
                    } else {
                      setUseCustomPaymentTerms(false);
                      setFormData({ ...formData, payment_terms: value, custom_payment_terms: '' });
                    }
                  }}
                >
                  <SelectTrigger data-testid="vendor-payment-terms-select">
                    <SelectValue placeholder="Select payment terms" />
                  </SelectTrigger>
                  <SelectContent position="popper" className="max-h-60">
                    {PAYMENT_TERMS_OPTIONS.map((option) => (
                      <SelectItem 
                        key={option.value} 
                        value={option.value}
                        className={option.value === '__other__' ? 'text-primary font-medium' : ''}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {useCustomPaymentTerms && (
                  <div className="mt-2">
                    <Input
                      placeholder="Enter custom payment terms"
                      value={formData.custom_payment_terms}
                      onChange={(e) => setFormData({ ...formData, custom_payment_terms: e.target.value })}
                      data-testid="vendor-custom-payment-terms-input"
                      className="border-primary"
                      autoFocus
                    />
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                  data-testid="vendor-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-vendor-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {selectedVendor ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Vendor</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{selectedVendor?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-vendor-btn">
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorsPage;
